/**
 * Timetables — working-hour templates with break assignments.
 *
 * Migrated from mock data to live API as part of Wave 3.2. Fetches
 * both Timetables and Breaks on mount: the list view needs break
 * names for the "Breaks" column, and the edit form needs the full
 * break catalog as selectable chips.
 *
 * Backend computes total_hours and net_hours server-side so the list
 * view reads them directly; the edit form still runs a local
 * calculator to give the operator instant feedback on shift duration
 * before save.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack,
  Cancel,
  DeleteOutlined,
  EditOutlined,
  RefreshOutlined,
  Save,
} from '@mui/icons-material';
import { ApiError, attendanceApi } from '../../../api';
import type {
  Break,
  Timetable,
  TimetableCreate,
} from '../../../api/attendance';
import { ConfirmDialog, StickyFormActions } from '../../../components/primitives';
import { translateApiError } from '../../../i18n/translateApiError';
import { fmtUnit } from '../../../utils/datetime';

const COLOR_OPTIONS = [
  '#2E65D4',
  '#4CAF50',
  '#FF9800',
  '#9C27B0',
  '#F44336',
  '#00BCD4',
  '#795548',
  '#607D8B',
];

const EMPTY_FORM: TimetableCreate = {
  name: '',
  type: 'fixed',
  clock_in: '09:00',
  clock_out: '18:00',
  core_start: '10:00',
  core_end: '15:00',
  flex_start: '07:00',
  flex_end: '19:00',
  required_minutes: 480,
  cross_midnight: false,
  break_ids: [],
  color: '#2E65D4',
};

const TYPE_COLORS: Record<string, string> = {
  fixed: '#2E65D4', flexitime: '#7B1FA2', flexible: '#00897B',
};

/**
 * Compute total shift duration + net working hours (after breaks) from
 * a set of form values. Mirrors the backend computation so the edit
 * form can show live feedback; the authoritative value on the list
 * view still comes from the server's total_hours / net_hours fields.
 */
function computeHours(
  clock_in: string,
  clock_out: string,
  cross_midnight: boolean,
  break_ids: string[],
  breaks: Break[],
): { total: number; net: number; breakMinutes: number } {
  const [inH, inM] = clock_in.split(':').map(Number);
  const [outH, outM] = clock_out.split(':').map(Number);
  let total = outH * 60 + outM - (inH * 60 + inM);
  if (cross_midnight && total <= 0) total += 24 * 60;
  if (total < 0) total = 0;
  const breakMinutes = break_ids.reduce(
    (sum, id) => sum + (breaks.find((b) => b.id === id)?.duration_min ?? 0),
    0,
  );
  return {
    total: +(total / 60).toFixed(2),
    net: +((total - breakMinutes) / 60).toFixed(2),
    breakMinutes,
  };
}

const Timetables: React.FC = () => {
  const { t } = useTranslation();
  const [timetables, setTimetables] = useState<Timetable[]>([]);
  const [breaks, setBreaks] = useState<Break[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [form, setForm] = useState<TimetableCreate>({ ...EMPTY_FORM });
  const isEditing = !!selectedId || isCreating;

  const [filterText, setFilterText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pendingDelete, setPendingDelete] = useState<Timetable | null>(null);

  // Load timetables + breaks in parallel so the list and edit form
  // both have what they need on mount.
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ttList, brkList] = await Promise.all([
        attendanceApi.timetablesApi.list(),
        attendanceApi.breaksApi.list(),
      ]);
      setTimetables(ttList);
      setBreaks(brkList);
    } catch (e) {
      const msg =
        e instanceof ApiError ? translateApiError(e, t) : t('attendance:timetables.loadFailed');
      setError(msg);
      setTimetables([]);
      setBreaks([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const filtered = useMemo(
    () =>
      timetables.filter((t) =>
        t.name.toLowerCase().includes(filterText.toLowerCase()),
      ),
    [timetables, filterText],
  );
  const paginated = useMemo(
    () =>
      filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  const handleEdit = (id: string) => {
    const t = timetables.find((x) => x.id === id);
    if (!t) return;
    setForm({
      name: t.name,
      type: t.type ?? 'fixed',
      clock_in: t.clock_in ?? '09:00',
      clock_out: t.clock_out ?? '18:00',
      core_start: t.core_start ?? '10:00',
      core_end: t.core_end ?? '15:00',
      flex_start: t.flex_start ?? '07:00',
      flex_end: t.flex_end ?? '19:00',
      required_minutes: t.required_minutes ?? 480,
      cross_midnight: t.cross_midnight,
      break_ids: t.break_ids ?? [],
      color: t.color,
    });
    setSelectedId(id);
    setIsCreating(false);
  };

  const handleCreate = (type: 'fixed' | 'flexitime' | 'flexible' = 'fixed') => {
    setForm({ ...EMPTY_FORM, type, color: TYPE_COLORS[type] ?? EMPTY_FORM.color });
    setSelectedId(null);
    setIsCreating(true);
  };

  const handleCancel = () => {
    setSelectedId(null);
    setIsCreating(false);
  };

  const handleSave = async () => {
    if (!form.name?.trim()) return;
    setSaving(true);
    try {
      if (isCreating) {
        await attendanceApi.timetablesApi.create(form);
        setToast(t('attendance:timetables.toastCreated', { name: form.name }));
      } else if (selectedId) {
        await attendanceApi.timetablesApi.update(selectedId, form);
        setToast(t('attendance:timetables.toastUpdated', { name: form.name }));
      }
      handleCancel();
      await fetchAll();
    } catch (e) {
      const msg = e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.saveFailed');
      setToast(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await attendanceApi.timetablesApi.delete(pendingDelete.id);
      setToast(t('attendance:timetables.toastDeleted', { name: pendingDelete.name }));
      await fetchAll();
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? translateApiError(e, t)
          : t('attendance:timetables.deleteFailedReferenced');
      setToast(msg);
      throw e; // ConfirmDialog stays open for retry
    }
  };

  const { total: formTotal, net: formNet, breakMinutes } = computeHours(
    form.clock_in ?? '09:00',
    form.clock_out ?? '18:00',
    !!form.cross_midnight,
    form.break_ids ?? [],
    breaks,
  );

  // ----- Edit view -----
  if (isEditing) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          py: '12px',
          width: '100%',
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={handleCancel} sx={{ mr: 1 }}>
              <ArrowBack />
            </IconButton>
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, fontSize: '1.125rem', color: 'text.primary' }}
              >
                {isCreating ? t('attendance:timetables.dialog.newTitle') : t('attendance:timetables.dialog.editTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('attendance:timetables.dialog.description')}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                height: '100%',
              }}
            >
              <Box sx={{ px: 2.5, py: 1.25 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'text.primary' }}
                >
                  {t('attendance:timetables.dialog.workingHours')}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.7rem' }}
                >
                  {t('attendance:timetables.dialog.workingHoursHint')}
                </Typography>
              </Box>
              <CardContent sx={{ px: 2.5, pt: 0.75, pb: 2 }}>
                <Stack spacing={1.5}>
                  <TextField
                    label={t('attendance:timetables.nameLabel', { defaultValue: 'Timetable name' })}
                    value={form.name}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, name: e.target.value }))
                    }
                    size="small"
                    fullWidth
                    placeholder={t('attendance:timetables.namePlaceholder', { defaultValue: 'e.g. Standard Day' })}
                  />
                  <Chip
                    size="small"
                    label={t(`attendance:timetables.type.${form.type ?? 'fixed'}`)}
                    sx={{ alignSelf: 'flex-start', borderRadius: 1, bgcolor: TYPE_COLORS[form.type ?? 'fixed'], color: '#fff' }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {t(`attendance:timetables.type.${form.type ?? 'fixed'}Hint`)}
                  </Typography>

                  {(form.type ?? 'fixed') === 'fixed' && (
                  <Stack direction="row" spacing={1.5}>
                    <TextField
                      label={t('attendance:timetables.dialog.clockInTime')}
                      type="time"
                      value={form.clock_in ?? ''}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, clock_in: e.target.value }))
                      }
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label={t('attendance:timetables.dialog.clockOutTime')}
                      type="time"
                      value={form.clock_out ?? ''}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, clock_out: e.target.value }))
                      }
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Stack>
                  )}

                  {(form.type === 'flexitime') && (
                    <Stack direction="row" spacing={1.5}>
                      <TextField
                        label={t('attendance:timetables.dialog.coreStart')}
                        type="time" value={form.core_start ?? ''}
                        onChange={(e) => setForm((p) => ({ ...p, core_start: e.target.value }))}
                        size="small" fullWidth InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        label={t('attendance:timetables.dialog.coreEnd')}
                        type="time" value={form.core_end ?? ''}
                        onChange={(e) => setForm((p) => ({ ...p, core_end: e.target.value }))}
                        size="small" fullWidth InputLabelProps={{ shrink: true }}
                      />
                    </Stack>
                  )}

                  {(form.type === 'flexitime' || form.type === 'flexible') && (
                    <>
                      <Stack direction="row" spacing={1.5}>
                        <TextField
                          label={t('attendance:timetables.dialog.flexStart')}
                          type="time" value={form.flex_start ?? ''}
                          onChange={(e) => setForm((p) => ({ ...p, flex_start: e.target.value }))}
                          size="small" fullWidth InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          label={t('attendance:timetables.dialog.flexEnd')}
                          type="time" value={form.flex_end ?? ''}
                          onChange={(e) => setForm((p) => ({ ...p, flex_end: e.target.value }))}
                          size="small" fullWidth InputLabelProps={{ shrink: true }}
                        />
                      </Stack>
                      <TextField
                        label={t('attendance:timetables.dialog.requiredHours')}
                        type="number"
                        value={form.required_minutes != null ? form.required_minutes / 60 : ''}
                        onChange={(e) => setForm((p) => ({ ...p, required_minutes: Math.round(Number(e.target.value) * 60) }))}
                        size="small" fullWidth
                        inputProps={{ min: 0, max: 24, step: 0.5 }}
                        helperText={t('attendance:timetables.dialog.requiredHoursHint')}
                      />
                    </>
                  )}

                  {(form.type ?? 'fixed') === 'fixed' && (
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={!!form.cross_midnight}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            cross_midnight: e.target.checked,
                          }))
                        }
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                          {t('attendance:timetables.dialog.crossesMidnight')}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: '0.7rem' }}
                        >
                          {t('attendance:timetables.dialog.crossesMidnightHint')}
                        </Typography>
                      </Box>
                    }
                  />
                  )}
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.75 }}
                    >
                      {t('attendance:timetables.dialog.colorTag')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.75 }}>
                      {COLOR_OPTIONS.map((c) => (
                        <Box
                          key={c}
                          onClick={() => setForm((p) => ({ ...p, color: c }))}
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            bgcolor: c,
                            cursor: 'pointer',
                            border: form.color === c ? '2px solid' : '2px solid transparent',
                            borderColor:
                              form.color === c ? 'text.primary' : 'transparent',
                            transition: 'border-color 80ms ease',
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                height: '100%',
              }}
            >
              <Box sx={{ px: 2.5, py: 1.25 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'text.primary' }}
                >
                  {t('attendance:timetables.dialog.breaksSummary')}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.7rem' }}
                >
                  {t('attendance:timetables.dialog.breaksSummaryHint')}
                </Typography>
              </Box>
              <CardContent sx={{ px: 2.5, pt: 0.75, pb: 2 }}>
                <Stack spacing={1.5}>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.75 }}
                    >
                      {t('attendance:timetables.dialog.assignedBreaks')}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.75,
                      }}
                    >
                      {breaks.length === 0 && (
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          sx={{ fontSize: '0.75rem' }}
                        >
                          {t('attendance:timetables.dialog.noBreaksYet')}
                        </Typography>
                      )}
                      {breaks.map((b) => {
                        const selected = (form.break_ids ?? []).includes(b.id);
                        return (
                          <Chip
                            key={b.id}
                            label={`${b.name} (${b.duration_min}m)`}
                            size="small"
                            variant={selected ? 'filled' : 'outlined'}
                            color={selected ? 'primary' : 'default'}
                            onClick={() =>
                              setForm((p) => ({
                                ...p,
                                break_ids: selected
                                  ? (p.break_ids ?? []).filter((x) => x !== b.id)
                                  : [...(p.break_ids ?? []), b.id],
                              }))
                            }
                            sx={{
                              cursor: 'pointer',
                              borderRadius: 1,
                              fontSize: '0.75rem',
                            }}
                          />
                        );
                      })}
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 0.75, display: 'block', fontSize: '0.68rem' }}
                    >
                      {t('attendance:timetables.dialog.clickToAddRemove')}{' '}
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{
                          color: 'primary.main',
                          cursor: 'pointer',
                          fontWeight: 600,
                          '&:hover': { textDecoration: 'underline' },
                        }}
                        onClick={() =>
                          (window.location.href = '/attendance/shifts/breaks')
                        }
                      >
                        {t('attendance:timetables.dialog.createOne')}
                      </Typography>
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'grey.50',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 1 }}
                    >
                      {t('attendance:timetables.dialog.summary')}
                    </Typography>
                    <Stack spacing={1}>
                      <Box
                        sx={{ display: 'flex', justifyContent: 'space-between' }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {t('attendance:timetables.dialog.shiftDuration')}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {formTotal} hours
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: 'flex', justifyContent: 'space-between' }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {t('attendance:timetables.dialog.totalBreakTime')}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {breakMinutes} min
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          pt: 0.75,
                          borderTop: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 600, color: 'text.primary' }}
                        >
                          {t('attendance:timetables.dialog.netWorkingHours')}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, color: 'primary.main' }}
                        >
                          {formNet} hours
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Sticky Save bar */}
        <StickyFormActions
  primary={(
    <Button
    variant="contained"
    size="small"
    startIcon={saving ? <CircularProgress size={14} /> : <Save />}
    onClick={handleSave}
    disabled={!form.name?.trim() || saving}
    sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}
    >
    {isCreating ? t('attendance:timetables.createTimetable') : t('attendance:timetables.saveChanges')}
    </Button>
  )}
  secondary={(
    <Button
    variant="outlined"
    size="small"
    startIcon={<Cancel />}
    onClick={handleCancel}
    disabled={saving}
    sx={{
    borderRadius: 1,
    textTransform: 'none',
    fontSize: '0.875rem',
    color: 'text.secondary',
    borderColor: 'divider',
    }}
    >
    {t('common:actions.cancel')}
    </Button>
  )}
/>

        <Snackbar
          open={!!toast}
          autoHideDuration={4000}
          onClose={() => setToast(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            severity="info"
            onClose={() => setToast(null)}
            sx={{ width: '100%' }}
          >
            {toast}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  // ----- List view -----
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        py: '12px',
        width: '100%',
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          mb: 1.5,
          gap: 1.5,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, fontSize: '1.125rem', color: 'text.primary' }}
          >
            {t('attendance:timetables.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('attendance:timetables.subtitleDetail')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title={t('common:actions.refresh')}>
            <IconButton
              size="small"
              onClick={fetchAll}
              disabled={loading}
              sx={{
                border: '1px solid rgba(0,0,0,0.2)',
                borderRadius: 1,
                bgcolor: '#ffffff',
                '&:hover': { bgcolor: '#f5f5f5' },
              }}
            >
              <RefreshOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleCreate('fixed')}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}
          >
            {t('attendance:timetables.addFixed')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleCreate('flexitime')}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}
          >
            {t('attendance:timetables.addFlexitime')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleCreate('flexible')}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}
          >
            {t('attendance:timetables.addFlexible')}
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <TextField
          variant="outlined"
          label={t('attendance:timetables.filterLabel')}
          value={filterText}
          onChange={(e) => {
            setFilterText(e.target.value);
            setPage(0);
          }}
          size="small"
          sx={{ width: 300 }}
        />
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filtered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{
            '.MuiTablePagination-toolbar': { pl: 2 },
            '.MuiTablePagination-displayedRows': { m: 0 },
          }}
        />
      </Box>

      {loading ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}
        >
          <Table>
            <TableHead
              sx={{
                bgcolor: 'grey.100',
                '& .MuiTableCell-root': { fontWeight: 600 },
              }}
            >
              <TableRow>
                <TableCell>{t('attendance:timetables.colName', { defaultValue: 'Timetable' })}</TableCell>
                <TableCell>{t('attendance:timeEntries.colClockIn')}</TableCell>
                <TableCell>{t('attendance:timeEntries.colClockOut')}</TableCell>
                <TableCell>{t('attendance:timetables.colBreaks', { defaultValue: 'Breaks' })}</TableCell>
                <TableCell align="center">{t('attendance:timetables.colNetHours', { defaultValue: 'Net hours' })}</TableCell>
                <TableCell align="center">{t('attendance:timetables.colActions', { defaultValue: 'Actions' })}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map((tt) => (
                <TableRow
                  key={tt.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleEdit(tt.id)}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: tt.color,
                          flexShrink: 0,
                        }}
                      />
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: 'primary.main',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          {tt.name}
                        </Typography>
                        {tt.cross_midnight && (
                          <Chip
                            label="Night"
                            size="small"
                            sx={{
                              height: 16,
                              fontSize: '0.6rem',
                              borderRadius: 0.5,
                              ml: 0.5,
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {tt.type === 'flexible'
                        ? t('attendance:timetables.type.flexible')
                        : tt.type === 'flexitime'
                          ? (tt.core_start ?? '—')
                          : (tt.clock_in ?? '—')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {tt.type === 'flexible'
                        ? fmtUnit((tt.required_minutes ?? 0) / 60, 'hour', undefined, 2)
                        : tt.type === 'flexitime'
                          ? (tt.core_end ?? '—')
                          : (tt.clock_out ?? '—')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {(tt.break_ids ?? []).map((bid) => {
                        const b = breaks.find((x) => x.id === bid);
                        return b ? (
                          <Chip
                            key={bid}
                            label={b.name}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              borderRadius: 1,
                            }}
                          />
                        ) : null;
                      })}
                      {(tt.break_ids ?? []).length === 0 && (
                        <Typography variant="caption" color="text.disabled">
                          None
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, color: 'primary.main' }}
                    >
                      {tt.net_hours}h
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Stack
                      direction="row"
                      spacing={0.5}
                      justifyContent="center"
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(t.id);
                        }}
                      >
                        <EditOutlined sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPendingDelete(t);
                        }}
                        sx={{ '&:hover': { color: 'error.main' } }}
                      >
                        <DeleteOutlined sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {paginated.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {filterText
                        ? t('attendance:timetables.noMatch')
                        : t('attendance:timetables.noDefined')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        tone="danger"
        title={t('attendance:timetables.deleteTitle')}
        description={t('attendance:timetables.deleteDescription')}
        subject={
          pendingDelete && (
            <Box sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
              {pendingDelete.name}
            </Box>
          )
        }
        confirmLabel={t('common:actions.delete')}
        confirmLoadingLabel={t('common:status.deleting')}
        onConfirm={handleConfirmDelete}
        onClose={() => setPendingDelete(null)}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          onClose={() => setToast(null)}
          sx={{ width: '100%' }}
        >
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Timetables;
